import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../hooks/useAppDispatch";
import { GeneralStyles } from "../../../assets/css/general.styles";
import { MenuItem, Checkbox, FormControlLabel, Grid } from "@mui/material";
import GridStyles from "../../../assets/css/grid.styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CustomButton from "../../../components/customButton/customButton";
import CustomTextField from "../../../components/customTextField/customTextField";
import { 
  SAVE_SUCCESSFULLY,
  VALIDATION_ERROR_FIELDS_REQUIRED_MESSAGE, 
  VALIDATION_ERROR_NAME_UNIQUE_MESSAGE, 
  VALIDATION_ERROR_REQUIRED_MESSAGE,
} from "../../../constants/actions"; 
import CustomSelect from "../../../components/customSelect/customSelect";
import { toast } from "react-toastify"; 
import { minimumOrder } from "../../../constants/validations"; 
import { AreaTouched } from "./areaTouched";
import { AreaResource } from "../../../models/resource/areaResource";
import { getAreaById, saveArea } from "../../../reducers/areaReducer";
import { AreaModel } from "../../../models/models/areaModel";
import { getCountries } from "../../../reducers/countryReducer";
import { Countries } from "../../../constants/enums";
import Loader from "../../../components/loader";

const AreaDetailsForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const authToken = useSelector((state: RootState) => state.auth.token);
  const [touched, setTouched] = useState<AreaTouched>({}); 
  const area = useSelector((state: RootState) => state.area.area); 
   const countries = useSelector((state: RootState) => state.country.countries); 
  const [formData, setFormData] = useState<AreaResource>({
    id: 0,
    nameAr: "", 
    nameEn: "",  
    countryId:Countries.KuwaitId
  });

    useEffect(() => { 
        dispatch(getCountries());  
    }, []); 

  useEffect(() => {
    if (!authToken) {
      navigate("/Signin");
    }
  }, [authToken]); 

  useEffect(() => {
    if (id) {
      const itemId = parseInt(id, 10);
      dispatch(getAreaById({ id: itemId }));
    }
  }, [id]); 

  useEffect(() => {
    const itemData = area.data;
    if (itemData && id) {
      const data = itemData as AreaModel;
      setFormData({
        id: data.id || 0, 
        nameEn: data.nameEn || "",
        nameAr: data.nameAr || "", 
        countryId: data.countryId || Countries.KuwaitId,  
      });
    }
  }, [area.data]);  

  const handleSave = () => {
    if (
      formData.nameEn.trim() === ""  ||
      formData.nameEn === formData.nameAr ||
      formData.nameAr.trim() === "" ||
        formData.countryId == null 
    ) {
      let touchedFields = {
        nameEn: formData.nameEn.trim() === "" || formData.nameEn === formData.nameAr,
        nameAr: formData.nameAr.trim() === "" || formData.nameEn === formData.nameAr,
        country: formData.countryId === null, 
      };
      setTouched(touchedFields);
      toast.error(`${VALIDATION_ERROR_FIELDS_REQUIRED_MESSAGE}`);
      return;
    }

    dispatch(saveArea({ data: formData }))
      .unwrap()
      .then((response) => {
        const {success, message} = response;
        if (success) {
          toast.success(`${SAVE_SUCCESSFULLY}`);
          navigate("/Areas");
        }else{
            toast.warning(`${message}`);
        }
      })
      .catch((error) => {
        console.error("Failed to save item:", error);
      });
  }; 
  return (
    <form style={GeneralStyles.formContainerStyle}>
       {area.isLoading && <Loader />} 
      <Grid
        container
        spacing={2}
        sx={{ flexDirection: { xs: "column", sm: "row" } }}
      >
        <Grid item xs={12} sm={6} md={10}>
          <Grid item xs={12} sm={6} md={8}>
            {id ? (
              <h1 style={GridStyles.title}>
                Edit Area{" "}
                <b>
                    {formData.nameEn}
                </b>
              </h1>
            ) : (
              <h1 style={GridStyles.title}>Add Area</h1>
            )}
          </Grid> 
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <div style={GridStyles.addButtonContainer}>
            <CustomButton
              type="button"
              style={GridStyles.addButton}
              onClick={handleSave}
            >
              <CheckCircleIcon style={GridStyles.iconStyle} /> Save
            </CustomButton>
          </div>
        </Grid> 
        <Grid item xs={12} sm={6} md={12}>
            <Grid
                container
                spacing={2}
                sx={{ flexDirection: { xs: "column", sm: "row" } }}
            >
                <Grid item xs={12} sm={6} md={4}>
                <CustomTextField
                    label="Name (En)"
                    name="nameEn"
                    type="text"
                    value={formData.nameEn}
                    onChange={(e) =>
                    setFormData((prevData) => ({
                        ...prevData,
                        nameEn: e.target.value,
                    }))
                    }
                    required={true}
                   error={touched.nameEn && (formData.nameEn.trim() === "" || formData.nameEn === formData.nameAr )} 
                    helperText={
                      touched.nameEn && formData.nameEn.trim() === ""
                        ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                        : touched.nameEn &&
                          formData.nameAr === formData.nameEn
                        ? `${VALIDATION_ERROR_NAME_UNIQUE_MESSAGE}`
                        : ""
                    }
                />
                </Grid> 
                <Grid item xs={12} sm={6} md={4}>
                <CustomTextField
                    label="Name (Ar)"
                    name="nameAr"
                    type="text"
                    value={formData.nameAr}
                    onChange={(e) =>
                    setFormData((prevData) => ({
                        ...prevData,
                        nameAr: e.target.value,
                    }))
                    }
                    required={true}
                  error={touched.nameAr && (formData.nameAr.trim() === "" || formData.nameEn === formData.nameAr)}
                  helperText={
                  touched.nameAr && formData.nameAr.trim() === ""
                      ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}`
                      : touched.nameEn &&
                      formData.nameAr === formData.nameEn
                    ? `${VALIDATION_ERROR_NAME_UNIQUE_MESSAGE}`
                    : ""
                  }
                />
                </Grid> 
                <Grid item xs={12} sm={6} md={4}>
                    <CustomSelect
                        label="Country"
                        name="countryId"
                        type="text"
                        SelectProps={{
                            children: Array.isArray(countries.data) ? (
                            countries.data.map((item: any) => (
                                <MenuItem key={item.id} value={item.id}>
                                {item.nameEn}
                                </MenuItem>
                            ))
                            ) : countries.data instanceof Object ? (
                            <MenuItem
                                key={(countries.data as any).id}
                                value={(countries.data as any).id}
                            >
                                {(countries.data as any).nameEn}
                            </MenuItem>
                            ) : null,

                            IconComponent: () => null,
                            renderValue: (value: any) => {
                            if (Array.isArray(countries.data)) {
                                const selected = countries.data.filter(
                                (item: any) => value.includes(item.id)
                                );
                                return selected
                                .map((item) => item.nameEn)
                                .join(", ");
                            } else if (countries.data instanceof Object) {
                                return (countries.data as any).nameEn || "";
                            }
                            return "";
                            },
                        }}
                        value={formData.countryId || Countries.KuwaitId} 
                        onChange={(e) => setFormData((prevData) => ({ ...prevData, countryId: e.target.value as number}))}  
                        multiple={false}
                        required={true} 
                        disabled={true} 
                        error={touched.country && formData.countryId === null}
                        helperText={touched.country && formData.countryId === null ? `${VALIDATION_ERROR_REQUIRED_MESSAGE}` : ""} 
                    />
                </Grid> 
            </Grid>
        </Grid>  
      </Grid>
    </form>
  );
};

export default AreaDetailsForm;
